/**
 * TermsCheckbox - Required checkbox for Terms & Conditions acceptance
 *
 * Features:
 * - Accessible checkbox with clickable label
 * - Terms and Privacy links open in new tab
 * - Error state styling
 * - Keyboard accessible
 * - Screen-reader friendly
 */

import { Link } from 'react-router-dom';

export default function TermsCheckbox({
  checked,
  onChange,
  error = null,
  disabled = false,
  className = ''
}) {
  const handleChange = (e) => {
    if (!disabled) {
      onChange(e.target.checked);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (!disabled) {
        onChange(!checked);
      }
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label
        className={`flex items-start gap-3 cursor-pointer group ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {/* Custom Checkbox */}
        <div className="relative flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            checked={checked}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className="sr-only peer"
            aria-describedby={error ? 'terms-error' : undefined}
            aria-invalid={error ? 'true' : 'false'}
          />
          <div
            className={`w-5 h-5 rounded-md border-2 transition-all duration-200
              ${checked
                ? 'bg-[#06c] border-[#06c]'
                : error
                  ? 'border-red-400 bg-red-50'
                  : 'border-[#d2d2d7] bg-white group-hover:border-[#06c]'
              }
              ${!disabled && 'peer-focus-visible:ring-2 peer-focus-visible:ring-[#06c] peer-focus-visible:ring-offset-2'}
            `}
          >
            {/* Checkmark */}
            {checked && (
              <svg
                className="w-full h-full text-white p-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Label Text */}
        <span className="text-sm text-[#1d1d1f] leading-relaxed">
          I agree to the{' '}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#06c] hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-1 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </Link>
          {' '}and{' '}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#06c] hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-1 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </Link>
        </span>
      </label>

      {/* Error Message */}
      {error && (
        <p
          id="terms-error"
          className="text-sm text-red-600 flex items-center gap-1.5"
          role="alert"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}
